import { SUPPORT_FORMATTED_NUMBER } from "./messages";

export const JOB_NIMBUS_USER_ID = "jobnimbus";
export const SUNRUN_ORG_ID = "sunrun";
export const ABSOLUTE_EXTERIORS_ORG_ID = "absolute_exteriors";
export const MARCOR_CONSTRUCTION_ORG_ID = "marcor_construction";
export const MARCOR_SOLAR_ORG_ID = "marcor_solar-1";
export const ENSITE_ORG_ID = "ensite_services-1";
export const GOODCO_ORG_ID = "goodco_solar";

// Marcor runs as two orgs for licensing and payment, but one phone team serves both.
const PROXY_ORG_GROUPS: readonly (readonly string[])[] = [[MARCOR_CONSTRUCTION_ORG_ID, MARCOR_SOLAR_ORG_ID]];

export const getProxyOrgGroup = (organizationId: string): readonly string[] =>
  PROXY_ORG_GROUPS.find((group) => group.includes(organizationId)) ?? [organizationId];

export const getProxyOrgGroupKey = (organizationId: string): string =>
  getProxyOrgGroup(organizationId)[0] ?? organizationId;

export const listProxyOrgGroups = (): readonly (readonly string[])[] => PROXY_ORG_GROUPS;

// Secutor is Remi's contracted penetration-testing org. Its accounts deliberately fuzz
// endpoints, so their runtime errors are expected noise rather than actionable bugs.
export const SECUTOR_PENTESTERS_ORG_ID = "secutor_pentesters-1";
export const NON_EXISTENT_ID = "id_that_does_not_exist_r3M1_h4Ck";
export const DEFAULT_ACTIONABLE_TEAM_ID = "default";

// Used for sales licensing currently
export const STERLING_PERSON_ID = "cm6fhfpex0000kx9vchha4bni";
export const RENO_PERSON_ID = "user_2b2x6uI2IPzYWMsI4nhUh4qxvWK";

// Temporary grants for MCP SQL access, bypassing the capability check pending a
// least-privilege REMI_SQL_RUNNER role (ODD-9315). Remove once that role is assigned.
export const RENATO_VILLANUEVA_PERSON_ID = "c3uiiqtlmkgy9cdvodg3od6g";
export const AUSTIN_THAYN_PERSON_ID = "o7hicke93twgo5mfugjn0b7i";
export const TY_DICKSON_PERSON_ID = "lh5rafev6cyn4mhra0l0llei";

// Keys must match the seed roster's `${firstName} ${lastName}` — db-scripts looks each Person's phone up here.
export const DEV_PHONE_NUMBERS = {
  "Sam Wasden": "+12085138285",
  "Kevin Kinney": "+18019003102",
  "Joshua Talbot": "+18015770507",
  "Erica Stauffer": "+18017875769",
  "Nate Sirrine": "+18018286532",
  "Josh Probst": "+13852083206",
  "Mark Delorey": "+18019001464",
  "Carson Baughan": "+17044397527",
  "Nate Kolly": "+18015581855",
  "Katie Nuckols": "+18179398995",
  "Josh Chambers": "+18584328160",
  "Zach Getz": "+13852728257",
  "Caleb Dastrup": "+12402340680",
  "KangJin Kim": "+13852218754",
  "Brandon Jacobs": "+17208787110",
  "Evan Darcy": "+12027165848",
  "Ike Erdman": "+18014271558",
  "Kyler Griggs": "+18018561687",
  "Caleb Larson": "+12602427698",
  "Robison Rogers": "+18016696991",
  "Evelin Robinson": "+18013419674",
  "Meredith Von Feldt": "+17853938481",
  "Conner Kennedy": "+19707699157",
  "Kamila Turapova": "+13855674981",
  "Brayden Gibbons": "+13852702849",
} as const satisfies Record<string, string>;

const unownedDevelopmentPhoneNumbers = [
  "+14353399787",
  "+14693164264", // Kevin Kinney 2
  "+17024931130",
  "+13852774750",
  "+13152918653", // Twilio proxy test number
  "+17372654458", // Twilio proxy test number
  "+15204369307", // Twilio proxy test number
  "+13156503628", // Twilio proxy test number
  "+13852131399",
];

// Phone numbers not included in this array will not be able to be reached via SMS outside of production.
export const developmentPhoneNumbers: string[] = [
  ...Object.values(DEV_PHONE_NUMBERS),
  ...unownedDevelopmentPhoneNumbers,
];
export const dummyPhoneNumber = "+15555555555";

export const ENGINEERING_PHONE_NUMBER = "+13854214826";
/** Quo line where setters call homeowners who submitted a DTC lead */
export const DTC_SETTERS_LINE_NUMBER = "+14352926312";
/** Contractor & Compliance shared OpenPhone line for SMS to contractors */
export const CONTRACTOR_COMPLIANCE_SMS_FROM_NUMBER = "+13853360334";
/** Knock's Twilio sender: work order, @mention and contractor job texts come from this number. */
export const NOTIFICATION_PROVIDER_PHONE_NUMBER = "+19169148399";
/** Knock development's Twilio sender, so test texts and replies never touch the production line. */
export const DEVELOPMENT_NOTIFICATION_PROVIDER_PHONE_NUMBER = "+16615774891";
/** Contractor support Quo line, (385) 557-8587: the one number contractors call or text for help. */
export const CONTRACTOR_SUPPORT_LINE_PHONE_NUMBER = "+13855578587";
/** Remi Licensed Labor team's OpenPhone line */
export const REMI_LICENSED_LABOR_LINE_PHONE_NUMBER = "+13852471894";

/** E.164 phone numbers owned by Remi system accounts — never a real homeowner. */
export const REMI_SYSTEM_PHONE_NUMBERS: ReadonlySet<string> = new Set([
  SUPPORT_FORMATTED_NUMBER,
  ENGINEERING_PHONE_NUMBER,
  CONTRACTOR_COMPLIANCE_SMS_FROM_NUMBER,
  REMI_LICENSED_LABOR_LINE_PHONE_NUMBER,
]);

export const isSystemPhone = (e164Phone: string): boolean => REMI_SYSTEM_PHONE_NUMBERS.has(e164Phone);

export const REMI_ENGINEER_EMAIL = "engineering@remihq.com";
export const REMI_SALES_EMAIL = "sales-department@remihq.com";

export const REMI_SUPPORT_EMAIL = "support@remihq.com";

export const NOREPLY_FROM_EMAIL = "no-reply@remihq.com";

export const COMPLIANCE_FROM_EMAIL = "contractors@remihq.com";
export const COMPLIANCE_DISPLAY_NAME = "Remi Compliance";

export const INSURANCE_OPERATIONS_FROM_EMAIL = "operations_insurance@remihq.com";

export const CONTRACTOR_EXPORT_DISPLAY_NAME = "Remi Data";

export const REMI_SUPPORT_NUMBER = "916.999.7497";

export const UNKNOWN_PERSON_NAME = "Unknown";
export const UNKNOWN_ORGANIZATION_ID = "unknown-organization";
export const UNKNOWN_ORGANIZATION_NAME = "Unknown Organization";

// @sitevistops Slack subteam, pinged in #notis-pilot-site-visit-assigned when a
// SOLAR_REDESIGN SITE_VISIT is assigned to Inspectify or Frontier Claims.
export const SITE_VISIT_OPS_SLACK_USER_GROUP_ID = "S0B91SRMQ30";
